// Host-facing HTTP implementation of ShopApi against the real shop routes
// (/v1/shop storefront reads + /v1/shop-orders purchase lifecycle). Firebase-free:
// auth + base URL come from the injected BackendHttpDeps, so any host
// (PlaygroundHost, Telegram/Steam shells) can construct it.
// Do NOT import from barrel exports (shop/index.ts).

import {
  ShopApi,
  StorefrontResponse,
  StorefrontItem,
  ShopPurchaseResponse,
  ShopOrderHistoryResponse,
} from './ShopApi'
import { RundotApiError } from '../errors'
import { authedBackendFetch } from '../http/backendHttpDeps'
import type { BackendHttpDeps } from '../http/backendHttpDeps'

export interface HttpShopApiOptions {
  /** Version tag whose server config the storefront resolves (`tagId`), e.g. `private` in the playground. */
  versionTag?: string
  /**
   * Host-owned store checkout used only when an order pins a direct-purchase SKU.
   * MUST durably record the transaction against `orderId` before resolving, and
   * return that record instead of charging again on a later call — the reclaim
   * path treats a resolved checkout as proof the charge is no longer invisible.
   */
  directPurchaseCheckout?: (sku: string, orderId: string) => Promise<{
    provider: 'revenuecat' | 'steam'
    transactionId: string
  }>
  /** Best-effort cleanup after the server durably confirms the provider receipt. */
  directPurchaseCheckoutCompleted?: (orderId: string) => Promise<void> | void
}

// Mirrors client/services/shopDirectPurchase.ts: how long a "no charge found"
// answer stays untrustworthy after a store hand-off, because RevenueCat's
// purchases list lags the charge itself.
const CHARGE_SETTLE_MS = 2 * 60 * 1000
const CHECKOUT_ATTEMPT_KEY_PREFIX = 'rundot-shop-direct-attempt:'

/** Epoch ms of the last store hand-off for this order, or null if there was none. */
function readCheckoutAttempt(orderId: string): number | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(`${CHECKOUT_ATTEMPT_KEY_PREFIX}${orderId}`)
    if (raw === null) return null
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : 0
  } catch {
    return null
  }
}

function writeCheckoutAttempt(orderId: string): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(`${CHECKOUT_ATTEMPT_KEY_PREFIX}${orderId}`, String(Date.now()))
  } catch {
    // Best effort: without the marker the reclaim lookup still runs, it just
    // cannot tell a lagging null from a genuine one.
  }
}

function clearCheckoutAttempt(orderId: string): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.removeItem(`${CHECKOUT_ATTEMPT_KEY_PREFIX}${orderId}`)
  } catch {
    // Best effort.
  }
}

type DirectPurchaseCheckoutResult = Awaited<
  ReturnType<NonNullable<HttpShopApiOptions['directPurchaseCheckout']>>
>

interface DirectPurchaseCheckoutPreparation {
  directPurchase: DirectPurchaseCheckoutResult | null
  checkoutToken: string | null
}

/**
 * Purchase-flow error codes with a dedicated response shape
 * (server/cloud-run/src/controllers/shopOrder.controller.ts): these put the
 * machine code in `error` with `message` alongside, while every other shop
 * error puts the human message in `error` and the code in `code`.
 */
type PurchaseFlowErrorCode = 'stale_catalog' | 'insufficient_funds'

const PURCHASE_FLOW_CODES: ReadonlySet<string> = new Set<PurchaseFlowErrorCode>(
  ['stale_catalog', 'insufficient_funds'],
)

interface ParsedShopError {
  code: string
  message: string
  currentConfigId?: string
  balance?: number
  deficit?: number
}

function parseShopErrorBody(status: number, text: string): ParsedShopError {
  let body: Record<string, unknown>
  try {
    const parsed: unknown = JSON.parse(text)
    if (parsed === null || typeof parsed !== 'object') {
      return { code: 'unknown', message: text || `Request failed (${status})` }
    }
    body = parsed as Record<string, unknown>
  } catch {
    return { code: 'unknown', message: text || `Request failed (${status})` }
  }
  const rawError = typeof body.error === 'string' ? body.error : undefined
  const message = typeof body.message === 'string' ? body.message : undefined

  if (rawError && PURCHASE_FLOW_CODES.has(rawError)) {
    return {
      code: rawError,
      message: message ?? rawError,
      currentConfigId:
        typeof body.currentConfigId === 'string'
          ? body.currentConfigId
          : undefined,
      balance: typeof body.balance === 'number' ? body.balance : undefined,
      deficit: typeof body.deficit === 'number' ? body.deficit : undefined,
    }
  }

  return {
    code: typeof body.code === 'string' ? body.code : 'unknown',
    message: message ?? rawError ?? (text || `Request failed (${status})`),
  }
}

export class HttpShopApi implements ShopApi {
  private readonly gameId: string
  private readonly deps: BackendHttpDeps
  private readonly versionTag?: string
  private readonly directPurchaseCheckout?: HttpShopApiOptions['directPurchaseCheckout']
  private readonly directPurchaseCheckoutCompleted?: HttpShopApiOptions['directPurchaseCheckoutCompleted']
  private readonly pendingDirectPurchaseCheckouts = new Map<
    string,
    Promise<DirectPurchaseCheckoutResult>
  >()
  private cachedConfigId: string | null = null
  private fundingHintLogged = false

  constructor(
    gameId: string,
    deps: BackendHttpDeps,
    opts: HttpShopApiOptions = {},
  ) {
    this.gameId = gameId
    this.deps = deps
    this.versionTag = opts.versionTag
    this.directPurchaseCheckout = opts.directPurchaseCheckout
    this.directPurchaseCheckoutCompleted = opts.directPurchaseCheckoutCompleted
  }

  /** Drop the cached storefront configId; PlaygroundHost calls this on HMR config re-upload. */
  clearCatalogCache(): void {
    this.cachedConfigId = null
  }

  private tagQuery(): string {
    return this.versionTag
      ? `?tagId=${encodeURIComponent(this.versionTag)}`
      : ''
  }

  private async request<T>(
    method: 'GET' | 'POST',
    path: string,
    body?: Record<string, unknown>,
  ): Promise<T> {
    const response = await authedBackendFetch(this.deps, method, path, body)
    if (!response.ok) {
      throw this.toApiError(response.status, await response.text())
    }
    return response.json() as Promise<T>
  }

  private toApiError(status: number, text: string): RundotApiError {
    const parsed = parseShopErrorBody(status, text)

    if (parsed.code === 'stale_catalog') {
      if (parsed.currentConfigId) this.cachedConfigId = parsed.currentConfigId
      return new RundotApiError('stale_catalog', parsed.message, status)
    }

    if (parsed.code === 'insufficient_funds') {
      const detail = [
        parsed.balance !== undefined ? `balance: ${parsed.balance}` : null,
        parsed.deficit !== undefined ? `deficit: ${parsed.deficit}` : null,
      ]
        .filter(Boolean)
        .join(', ')
      if (!this.fundingHintLogged) {
        this.fundingHintLogged = true
        console.info(
          '[RUN:shop] Purchase failed: insufficient funds. Fund the test wallet via the in-playground store (RundotGameAPI.iap.openStore()).',
        )
      }
      return new RundotApiError(
        'insufficient_funds',
        detail ? `${parsed.message} (${detail})` : parsed.message,
        status,
      )
    }

    if (parsed.code === 'config-not-found') {
      return new RundotApiError(
        'config-not-found',
        'This game has no shop config. Add rundot/shop.config.json and reload — see the Shop API docs for the format.',
        status,
      )
    }

    return new RundotApiError(parsed.code, parsed.message, status)
  }

  private async fetchStorefront(): Promise<StorefrontResponse> {
    const storefront = await this.request<StorefrontResponse>(
      'GET',
      `/v1/shop/games/${this.gameId}/storefront${this.tagQuery()}`,
    )
    if (storefront.configId) this.cachedConfigId = storefront.configId
    return storefront
  }

  async getCatalog(
    includeInactive?: boolean,
    includeExpired?: boolean,
    includeUnreleased?: boolean,
  ): Promise<StorefrontResponse> {
    const storefront = await this.fetchStorefront()
    // Server ignores the include* flags — visibility is filtered client-side (matches H5ShopHandler).
    const now = Date.now()
    return {
      ...storefront,
      items: storefront.items.filter((item) => {
        if (!includeInactive && !item.active) return false
        if (!includeExpired && item.expiresAt !== null && item.expiresAt <= now)
          return false
        if (
          !includeUnreleased &&
          item.releasedAt !== null &&
          item.releasedAt > now
        )
          return false
        return true
      }),
    }
  }

  async getItemDetail(itemId: string): Promise<StorefrontItem> {
    const response = await this.request<{
      configId: string
      item: StorefrontItem
    }>(
      'GET',
      `/v1/shop/games/${this.gameId}/items/${encodeURIComponent(itemId)}${this.tagQuery()}`,
    )
    if (response.configId) this.cachedConfigId = response.configId
    return response.item
  }

  purchase(
    itemId: string,
    idempotencyKey: string,
  ): Promise<ShopPurchaseResponse> {
    return this.createOrder(itemId, idempotencyKey)
  }

  purchaseCollectionItem(
    collectionId: string,
    itemId: string,
    idempotencyKey: string,
  ): Promise<ShopPurchaseResponse> {
    return this.createOrder(itemId, idempotencyKey, collectionId)
  }

  private async createOrder(
    itemId: string,
    idempotencyKey: string,
    collectionId?: string,
  ): Promise<ShopPurchaseResponse> {
    if (!this.cachedConfigId) {
      await this.fetchStorefront()
    }
    if (!this.cachedConfigId) {
      throw new RundotApiError(
        'internal',
        'Storefront response did not include a configId; cannot pin a catalog version for purchase.',
        0,
      )
    }
    const created = await this.request<ShopPurchaseResponse>('POST', '/v1/shop-orders', {
      gameId: this.gameId,
      itemId,
      configId: this.cachedConfigId,
      idempotencyKey,
      ...(this.versionTag ? { tagId: this.versionTag } : {}),
      ...(collectionId ? { collectionId } : {}),
    })
    return this.completeDirectPurchase(created)
  }

  private async completeDirectPurchase(
    response: ShopPurchaseResponse,
  ): Promise<ShopPurchaseResponse> {
    const payment = response.order.directPurchase
    if (
      !payment
      || !['pending_payment', 'paid'].includes(response.order.status)
    ) {
      if (payment && response.order.status === 'fulfilled') {
        this.completeDirectPurchaseCheckout(response.order.orderId)
      }
      return response
    }

    let provider = payment.provider
    let transactionId = payment.transactionId
    if (!provider || !transactionId) {
      if (response.order.status === 'paid') {
        throw new RundotApiError(
          'direct-purchase-conflict',
          'Paid shop order is missing its provider transaction; contact support.',
          0,
        )
      }
      if (!this.directPurchaseCheckout) {
        throw new RundotApiError(
          'direct-purchase-unavailable',
          'This host cannot open the store checkout required by this shop item.',
          0,
        )
      }
      // A host learns a RevenueCat transaction id only when its checkout call
      // resolves, so a reload mid-charge leaves it with none. Preparing on the
      // server both recovers that receipt and serializes checkout ownership for
      // this user + SKU before another consumable order can open the store.
      // A checkout still in this process already owns a server generation; do
      // not rotate its token with another recovery request.
      const pendingCheckout = this.pendingDirectPurchaseCheckouts.get(
        response.order.orderId,
      )
      let checkout = pendingCheckout ? await pendingCheckout : null
      if (!checkout) {
        const preparation = await this.recoverDirectPurchase(response.order.orderId)
        checkout = preparation.directPurchase
        if (!checkout && !preparation.checkoutToken) {
          throw new RundotApiError(
            'direct-purchase-conflict',
            'The server did not return a checkout lease; retry the purchase.',
            0,
          )
        }
        if (!checkout) {
          // A null is only "no charge" once RevenueCat has had time to index one;
          // its purchases list lags the charge. Within the settle window of a
          // previous hand-off, make the caller retry rather than charge again.
          const attemptedAt = readCheckoutAttempt(response.order.orderId)
          if (attemptedAt !== null && Date.now() - attemptedAt < CHARGE_SETTLE_MS) {
            throw new RundotApiError(
              'direct-purchase-pending',
              'A purchase for this order may still be settling; try again shortly.',
              0,
            )
          }
          checkout = await this.checkoutDirectPurchase(
            response.order.orderId,
            payment.sku,
            preparation.checkoutToken!,
          )
        }
      }
      provider = checkout.provider
      transactionId = checkout.transactionId
    }

    const confirmed = await this.request<ShopPurchaseResponse>(
      'POST',
      `/v1/shop-orders/${encodeURIComponent(response.order.orderId)}/direct-purchase/confirm`,
      { provider, transactionId },
    )
    this.completeDirectPurchaseCheckout(response.order.orderId)
    return confirmed
  }

  /** Recover a prior charge and claim a generation for any new checkout. */
  private async recoverDirectPurchase(
    orderId: string,
  ): Promise<DirectPurchaseCheckoutPreparation> {
    return this.request<DirectPurchaseCheckoutPreparation>(
      'POST',
      `/v1/shop-orders/${encodeURIComponent(orderId)}/direct-purchase/recover`,
    )
  }

  private async markDirectPurchaseCheckoutStarted(
    orderId: string,
    checkoutToken: string,
  ): Promise<void> {
    await this.request<{ success: true }>(
      'POST',
      `/v1/shop-orders/${encodeURIComponent(orderId)}/direct-purchase/checkout-started`,
      { checkoutToken },
    )
  }

  private async releaseDirectPurchaseCheckout(
    orderId: string,
    checkoutToken: string,
  ): Promise<void> {
    await this.request<{ success: true }>(
      'POST',
      `/v1/shop-orders/${encodeURIComponent(orderId)}/direct-purchase/checkout-cancelled`,
      { checkoutToken },
    )
  }

  private completeDirectPurchaseCheckout(orderId: string): void {
    this.pendingDirectPurchaseCheckouts.delete(orderId)
    clearCheckoutAttempt(orderId)
    if (!this.directPurchaseCheckoutCompleted) return
    void Promise.resolve(
      this.directPurchaseCheckoutCompleted(orderId),
    ).catch(() => undefined)
  }

  private checkoutDirectPurchase(
    orderId: string,
    sku: string,
    checkoutToken: string,
  ): Promise<DirectPurchaseCheckoutResult> {
    const pending = this.pendingDirectPurchaseCheckouts.get(orderId)
    if (pending) return pending

    const checkout = (async () => {
      await this.markDirectPurchaseCheckoutStarted(orderId, checkoutToken)
      writeCheckoutAttempt(orderId)
      return this.directPurchaseCheckout!(sku, orderId)
    })()
    this.pendingDirectPurchaseCheckouts.set(orderId, checkout)
    void checkout.then(
      // Resolved means the transaction id is known and the host has durably
      // recorded it (see `directPurchaseCheckout`), so there is no longer an
      // invisible charge to guard against — a later reload must reach the host
      // and get its saved receipt rather than stall in the settle window.
      () => clearCheckoutAttempt(orderId),
      (error: unknown) => {
        if (this.pendingDirectPurchaseCheckouts.get(orderId) === checkout) {
          this.pendingDirectPurchaseCheckouts.delete(orderId)
        }
        // Dismissal is the one outcome that definitively took no money, so the
        // next attempt should reach the store instead of the settle-window wait.
        if (error instanceof RundotApiError && error.code === 'USER_CANCELLED') {
          clearCheckoutAttempt(orderId)
          // Best-effort cleanup: preserve the provider's cancellation result if
          // the ownership-release request itself cannot reach the backend.
          void this.releaseDirectPurchaseCheckout(
            orderId,
            checkoutToken,
          ).catch(() => undefined)
        }
      },
    )
    return checkout
  }

  getOrder(orderId: string): Promise<ShopPurchaseResponse> {
    return this.request<ShopPurchaseResponse>(
      'GET',
      `/v1/shop-orders/${encodeURIComponent(orderId)}`,
    )
  }

  getOrderHistory(options?: {
    limit?: number
  }): Promise<ShopOrderHistoryResponse> {
    const qs = options?.limit ? `?limit=${options.limit}` : ''
    return this.request<ShopOrderHistoryResponse>(
      'GET',
      `/v1/shop-orders/games/${this.gameId}/history${qs}`,
    )
  }

  requestRefund(
    orderId: string,
    reasonCode: string,
  ): Promise<ShopPurchaseResponse> {
    return this.request<ShopPurchaseResponse>(
      'POST',
      `/v1/shop-orders/${encodeURIComponent(orderId)}/refund`,
      {
        reasonCode,
      },
    )
  }
}
